import { Button } from "@octopusdeploy/design-system-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { EnvironmentResource, ProjectResource } from "@octopusdeploy/octopus-server-client";
import React, { useState } from "react";
import CopyValueToClipboard from "~/components/CopyToClipboardButton/CopyValueToClipboard";
import OkDialogLayout from "~/components/DialogLayout/OkDialogLayout";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import EnvironmentSelect from "~/components/form/EnvironmentSelect/EnvironmentSelect";
import { Select } from "~/components/form/index";
interface ClientIdentifierDialogProps {
    project: ProjectResource;
    environments: EnvironmentResource[];
}
export function PreviewClientIdentifierDialog({ project, environments }: ClientIdentifierDialogProps) {
    const [environmentId, setEnvironmentId] = useState<string | undefined>(environments.length > 0 ? environments[0].Id : undefined);
    const [tenantId, setTenantId] = useState<string>();
    const { error: getTenantsError, isLoading: getTenantsBusy, result: tenants, } = useInlineStatusQuery(async (repository) => {
        if (!environmentId) {
            return [];
        }
        return await repository.FeatureToggles.getTenantsConnectedToProjectAndEnvironment(project, environmentId);
    }, [project, environmentId], "Get Tenants And Environments for Client Identifier Preview", { initialResult: [] });
    const { error: getClientIdentifierError, isLoading: getClientIdentifierBusy, result: clientIdentifier, refetch: regenerateClientIdentifier, } = useInlineStatusQuery(async (repository) => {
        if (!environmentId) {
            return undefined;
        }
        const response = await repository.FeatureToggles.generateClientIdentifier(project, environmentId, tenantId);
        return response.ClientIdentifier;
    }, [project, environmentId, tenantId], "Get Client Identifier Preview");
    function getErrors() {
        const errors = [];
        if (getTenantsError) {
            errors.push(getTenantsError);
        }
        if (getClientIdentifierError) {
            errors.push(getClientIdentifierError);
        }
        return errors;
    }
    return (<OkDialogLayout title={"Preview Client Identifiers"} busy={getTenantsBusy || getClientIdentifierBusy} errors={getErrors()} onOkClick={() => { }} hideCancel={true}>
            <React.Fragment>
                <>
                    <p>Client identifiers allow your software to access Octopus Feature Toggles.</p>
                    <p>
                        Octopus provides the <b>Octopus.FeatureToggles.ClientIdentifier</b> deployment variable for you to merge into your application configuration.
                    </p>
                    <p>If you wish to use Octopus Feature Toggles in applications not deployed by Octopus, or wish to otherwise provide the client identifier to your client software, you can generate an appropriate identifier below.</p>
                    <p>Note that using this generated token will not resolve any toggles that have minimum release versions defined.</p>
                    <EnvironmentSelect label={"Environment"} environments={environments} value={environmentId} onChange={setEnvironmentId}/>
                    {tenants.length > 0 && (<Select label={"Tenant"} sortItems={false} value={tenantId} items={tenants.map((e) => ({ value: e.TenantId, text: e.Name }))} onChange={(selected) => {
                setTenantId(selected);
            }} allowClear={true}/>)}
                    <Button label={"Preview"} onClick={() => regenerateClientIdentifier()} importance="primary" disabled={!environmentId}/>
                    {clientIdentifier && (<div>
                            <p>
                                Your client identifier for <b>{environments.find((x) => x.Id === environmentId)?.Name}</b>
                                {tenantId ? (<>
                                        {" "}
                                        and <b>{tenants.find((x) => x.TenantId === tenantId)?.Name}</b>
                                    </>) : null}{" "}
                                is:
                            </p>
                            <CopyValueToClipboard value={clientIdentifier}>
                                <p style={{ maxWidth: 540, wordWrap: "break-word" }}>{clientIdentifier}</p>
                            </CopyValueToClipboard>
                            <ExternalLink href={"https://oc.to/UsingClientIdentifiers"}>Using Feature Toggle Client Identifiers</ExternalLink>.
                        </div>)}
                </>
            </React.Fragment>
        </OkDialogLayout>);
}
